import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const BestTimeVisitUganda = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Best Time to Visit Uganda | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Best Time to Visit Uganda: Month by Month Guide";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/8u8WjGG.jpg"
            alt="Uganda Seasons"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/ssIyPUG.jpg"
                alt="Esther Nakato"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Esther Nakato</h3>
                <p className="text-white/80 text-sm">Travel Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                Travel Tips
              </div>
              
              <h1 className="text-4xl font-bold">Best Time to Visit Uganda: Month by Month Guide</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 8, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  12 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Uganda
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Planning your trip to Uganda requires careful consideration of weather patterns and wildlife 
                  viewing opportunities. This comprehensive guide will help you choose the perfect time for 
                  your Ugandan adventure.
                </p>

                <h2>Dry Seasons (December-February & June-August)</h2>
                <p>
                  Uganda's dry seasons offer the best conditions for most activities:
                </p>
                <ul>
                  <li>Ideal for gorilla trekking with drier trails</li>
                  <li>Better wildlife viewing as animals gather around water sources</li>
                  <li>Clear skies perfect for photography</li>
                  <li>More comfortable road conditions</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/WqH4Bly.jpg" 
                    alt="Wildlife in dry season"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Wildlife gathering at a waterhole during the dry season
                  </p>
                </div>

                <h2>Wet Seasons (March-May & September-November)</h2>
                <p>
                  While wet seasons can be challenging, they offer unique advantages:
                </p>
                <ul>
                  <li>Lush, green landscapes ideal for photography</li>
                  <li>Better bird watching opportunities</li>
                  <li>Lower prices and fewer tourists</li>
                  <li>Dramatic skies and waterfalls at full flow</li>
                </ul>

                <h2>Month-by-Month Breakdown</h2>
                
                <h3>December-February</h3>
                <p>
                  Peak season for tourism. Excellent conditions for:
                </p>
                <ul>
                  <li>Gorilla and chimpanzee trekking</li>
                  <li>Game drives in savanna parks</li>
                  <li>Hiking and mountain climbing</li>
                </ul>

                <h3>March-May</h3>
                <p>
                  Long rains season. Consider these activities:
                </p>
                <ul>
                  <li>Bird watching</li>
                  <li>Cultural experiences</li>
                  <li>City tours</li>
                </ul>

                <h3>June-August</h3>
                <p>
                  Another dry season with excellent conditions:
                </p>
                <ul>
                  <li>All wildlife activities</li>
                  <li>Hiking and outdoor adventures</li>
                  <li>Cultural festivals</li>
                </ul>

                <h3>September-November</h3>
                <p>
                  Short rains with intermittent dry spells:
                </p>
                <ul>
                  <li>Mixed wildlife viewing opportunities</li>
                  <li>Photography of lush landscapes</li>
                  <li>Lower season rates</li>
                </ul>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Travel Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Book accommodations and permits well in advance for dry seasons</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Pack appropriate gear for your chosen season</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Consider combining different regions for varied experiences</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Be flexible with your itinerary during shoulder seasons</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Travel Planning", "Best Time", "Seasons", "Wildlife Viewing", "Weather"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Best Time to Visit Uganda: Month by Month Guide"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Best Time to Visit Uganda: Month by Month Guide"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Best Time to Visit Uganda: Month by Month Guide"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/ssIyPUG.jpg"
                      alt="Esther Nakato"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Esther Nakato</h3>
                      <p className="text-gray-600">Travel Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Esther has been organizing tours across Uganda for over 6 years and specializes in 
                    creating perfectly timed itineraries for optimal experiences.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#dry-seasons" className="block text-gray-600 hover:text-ta-green">Dry Seasons</a>
                    <a href="#wet-seasons" className="block text-gray-600 hover:text-ta-green">Wet Seasons</a>
                    <a href="#month-breakdown" className="block text-gray-600 hover:text-ta-green">Month-by-Month Guide</a>
                    <a href="#travel-tips" className="block text-gray-600 hover:text-ta-green">Travel Tips</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/ultimate-guide-gorilla-trekking" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/LUalhrs.jpg"
                          alt="Gorilla Trekking Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Ultimate Guide to Gorilla Trekking
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/safari-photography-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/Rl4EEiV.jpg"
                          alt="Safari Photography Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            The Complete Safari Photography Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">10 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BestTimeVisitUganda;